import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, effect } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, Subject, first, takeUntil } from 'rxjs';

import { validatorPatterns } from '@app/constants';
import { IRegistration } from '@data/services/registration/registration.interface';
import { IOktaRegistrationEligibilityResponse, IOktaRegistrationUser } from '@data/services/registration/okta-registration.interface';
import { RegistrationService } from '@data/services/registration/registration.service';
import { IValidateUrl } from '@data/services/registration/validate-url.interface';
import { environment } from '@env/environment';
import { UserAgreementComponent } from '@modules/registration/components/user-agreement/user-agreement.component';
import { ConfirmedValidator } from '@shared/helper/confirmed.validator';
import { CommonService } from '@shared/services/common/common.service';

import * as stateData from '@data/states.json';
import { ToastMsgService } from '@shared/services/toastr/toast-msg.service';

@Component({
  standalone: false,
  selector: 'app-user-registration',
  templateUrl: './user-registration.component.html',
  styleUrls: ['./user-registration.component.scss']
})
export class UserRegistrationComponent implements OnInit, OnDestroy {

  private _componentDestroyed$ = new Subject<boolean>();
  private _registrationPageInitialized: boolean = false;

  private _lastEnteredNpn: string = '';
  private _loginPattern = /^[a-zA-Z0-9]+$/;
  private _namePattern = /^[a-zA-Z]+[a-zA-Z \-]*$/;
  private _pwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d])(?=.*[!@#$&*])[A-Za-z\d!@#$&*].{7,}$/;
  private _phonePattern = /^[\(\)\d \-]+$/;
  private _numberOnlyPattern = /^[0-9]+$/;
  private _token: string;
  private _email: string;

  public accountId: number;
  public accountOwnerName: string;
  public accountOwnerEmail: string;
  public accountOwnerPhone: string;

  public displayAcctInfo: boolean = false;
  public displayInvalidAcct: boolean = false;
  public displayRoleExpired: boolean = false;
  public hasError: boolean = false;

  public disabledAccount: boolean = false;

  public componentLoaded: boolean = false;
  public inProgress: boolean = false;
  public loadingMessage: string = '';

  public logo: string = 'assets/images/dummy-navBar-logo-transparent.png';
  public oktaRegistrationEnabled: boolean = false;
  public registrationAttempt: boolean = false;
  public registrationStatus: string;
  public statesList: Array<any>;
  public userRegistrationForm: FormGroup;
  public registrationMessage$ = new BehaviorSubject<string>('');
  public eligibilityMessage: string = '';
  public eligibilityBlocked: boolean = false;
  public eligibilityCheckComplete: boolean = false;
  public eligibilityCheckInProgress: boolean = false;
  public showLoginField: boolean = true;
  public showPasswordFields: boolean = true;
  public supportContactHref: string = 'mailto:support@leadadvantagepro.com';
  public loginFormHref: string = environment.authClient;
  public showLoginCta: boolean = false;

  private _oktaUserId: string = '';
  private _oktaExists: boolean = false;
  public hidePassword: boolean = true;
  public hideConfirmPassword: boolean = true;

  public login: FormControl = new FormControl('', [Validators.required, Validators.pattern(this._loginPattern), Validators.minLength(6), Validators.maxLength(15)]);
  public password: FormControl = new FormControl('', [Validators.required, Validators.pattern(this._pwordPattern)]);
  public confirmPwd: FormControl = new FormControl('');
  public fname: FormControl = new FormControl('', [Validators.required, Validators.pattern(this._namePattern)]);
  public lname: FormControl = new FormControl('', [Validators.required, Validators.pattern(this._namePattern)]);
  public email: FormControl = new FormControl('', [Validators.required, Validators.pattern(validatorPatterns.EMAIL)]);
  public address1: FormControl = new FormControl('', [Validators.required]);
  public city: FormControl = new FormControl('', [Validators.required]);
  public state_id: FormControl = new FormControl('', [Validators.required]);
  public zip: FormControl = new FormControl('', [Validators.required, Validators.pattern('^[0-9]{5}$')]);
  public phone: FormControl = new FormControl('', [Validators.pattern(this._phonePattern)]);
  public mphone: FormControl = new FormControl('', [Validators.pattern(this._phonePattern)]);
  public fphone: FormControl = new FormControl('', [Validators.pattern(this._phonePattern)]);
  public npn: FormControl = new FormControl('', [Validators.required, Validators.pattern(this._numberOnlyPattern)]);
  public hasNoNPN: FormControl = new FormControl(false);
  constructor(
    private _activeRoute: ActivatedRoute,
    private _dialog: MatDialog,
    private _formBuilder: FormBuilder,
    private _commonService: CommonService,
    private _registrationService: RegistrationService,
    private _toastMsg: ToastMsgService,
  ) {

    this.statesList =  (stateData as any).default;
    this.userRegistrationForm = this._formBuilder.group({
      // controls
      login: this.login,
      password: this.password,
      confirmPwd: this.confirmPwd,
      company: new FormControl(''),
      fname: this.fname,
      lname: this.lname,
      email: this.email,
      address1: this.address1,
      address2: new FormControl(''),
      city: this.city,
      state_id: this.state_id,
      zip: this.zip,
      phone: this.phone,
      mphone: this.mphone,
      fphone: this.fphone,
      npn: this.npn,
      hasNoNPN: this.hasNoNPN,
      website: new FormControl(''),
		}, {
      // options
      validators: [
        (formGroup: FormGroup) => {
          if (this.npn.hasError('pattern'))
            return;

          if (this.npn.value.length === 0 && this.hasNoNPN.value === false)
            this.npn.setErrors({ missingNPN: true });
          else
            this.npn.setErrors(null);

          return;
        },
        ConfirmedValidator('password', 'confirmPwd')
      ]
    });

    effect(() => {
      if (!this._commonService.oktaRegistrationFlagLoaded() || this._registrationPageInitialized) {
        return;
      }

      this.oktaRegistrationEnabled = this._commonService.oktaRegistrationEnabled();
      this._registrationPageInitialized = true;
      this._initializeRegistrationPage();
      console.log("okta" + this.oktaRegistrationEnabled);
    });

  }


  ngOnInit(): void {
    this.hasNoNPN.valueChanges
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe({
        next: (checked) => {
          if (checked) {
            this._lastEnteredNpn = this.npn.value;
            this.npn.setValue('', { emitEvent: false });

          } else {
            this.npn.setValue(this._lastEnteredNpn, { emitEvent: false });

          }
        }
      });

    this.userRegistrationForm.valueChanges
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe({
        next: () => {
          if (this.userRegistrationForm.value.phone != "" || this.userRegistrationForm.value.mphone != "") {
            this.userRegistrationForm.get('phone')?.clearValidators();
            this.userRegistrationForm.get('mphone')?.clearValidators();
            this.userRegistrationForm.get('phone')?.setErrors(null);
            this.userRegistrationForm.get('mphone')?.setErrors(null);
          }
          this.hasError = false;
          this.registrationMessage$.next('');
        }
      });

  }

  public checkRegistrationEligibility(): void {
    if (!this.oktaRegistrationEnabled) {
      return;
    }

    this.email.markAsTouched();
    this.email.updateValueAndValidity({ emitEvent: false });
    this.registrationAttempt = false;
    this.registrationMessage$.next('');

    if (this.email.invalid) {
      this._resetEligibilityResult();
      this.eligibilityMessage = 'Enter a valid email address to continue.';
      this.eligibilityBlocked = true;
      return;
    }

    const emailValue = (this.email.value || '').trim().toLowerCase();
    this.eligibilityCheckInProgress = true;
    this.eligibilityMessage = '';
    this.eligibilityBlocked = false;
    this.showLoginCta = false;

    this._registrationService.checkRegistrationEligibility(emailValue)
      .pipe(first())
      .subscribe({
        next: (response: IOktaRegistrationEligibilityResponse) => {
          this._handleEligibilityResponse(response, emailValue);
        },
        error: () => {
          this._resetEligibilityResult();
          this.eligibilityBlocked = true;
          this.eligibilityMessage = 'We could not verify this email right now. Please try again or contact support.';
        }
      })
      .add(() => {
        this.eligibilityCheckInProgress = false;
      });
  }

  public resetEligibilityCheck(): void {
    this.email.enable({ emitEvent: false });
    this._resetEligibilityResult();
    this.eligibilityMessage = '';
  }

  public setLogo(logoSource?: string) {
    this.logo = logoSource ? logoSource : this.logo;
  }

  public getPasswordStrength(): number {
    const value = this.password.value || '';

    if (!value)
      return 0;

    const checks = [
      value.length >= 8,
      /[A-Z]/.test(value),
      /[a-z]/.test(value),
      /\d/.test(value),
      /[!@#$&*]/.test(value),
    ];

    return checks.filter(Boolean).length;
  }

  public getPasswordStrengthLabel(): string {
    const score = this.getPasswordStrength();

    if (score >= 5)
      return 'Strong';

    if (score >= 3)
      return 'Moderate';

    if (score > 0)
      return 'Weak';

    return 'Not started';
  }

  public getPasswordRequirements(): Array<{label: string; met: boolean}> {
    const value = this.password.value || '';
    return [
      { label: 'At least 8 characters long', met: value.length >= 8 },
      { label: 'At least 1 capital letter', met: /[A-Z]/.test(value) },
      { label: 'At least 1 lower case letter', met: /[a-z]/.test(value) },
      { label: 'At least 1 number', met: /\d/.test(value) },
      { label: 'At least 1 special character (!,@,#,$,&,*)', met: /[!@#$&*]/.test(value) },
    ];
  }

  public submitForm(): boolean {
    if (this.oktaRegistrationEnabled && !this.eligibilityCheckComplete) {
      this.hasError = true;
      this._showRegistrationMessage('Please complete Step 1 email verification before submitting.');
      return false;
    }

    if (this.userRegistrationForm.value.phone == "" && this.userRegistrationForm.value.mphone == "") {
      this.updatePhoneValidation();
    }
    this.registrationAttempt = false;
    this.userRegistrationForm.markAllAsTouched();
    this.registrationMessage$.next('');

    const formValues = this.userRegistrationForm.getRawValue() as IRegistration;

    if (this.userRegistrationForm.valid) {
      this.hasError = false;
      return this._checkUserAgreement(formValues);
    } else {
      this.hasError = true;
      this._showRegistrationMessage('Please enter all the required fields before submission.');

      return false;
    }
  }

  private _checkURLValidation(cobrandAccount : IValidateUrl) {
    const msg = cobrandAccount.message ? cobrandAccount.message : '';
    const cobrandInfo = cobrandAccount?.cobrand;
    const cobrandOwner = cobrandAccount?.user;

    if (msg.indexOf("Role Expired") > -1) {
      this.displayRoleExpired = true;

    } else if (cobrandInfo && cobrandOwner) {
      this.displayAcctInfo = true;

      this.accountId = cobrandOwner.account_id;
      this.accountOwnerName = cobrandOwner.fname + " " + cobrandOwner.lname;
      this.accountOwnerEmail = cobrandOwner.default_email.email;
      this.accountOwnerPhone = cobrandOwner.default_phone.phone;

      // CoBranding Agency Logo Update
      if (cobrandInfo) {
        this.setLogo(cobrandInfo.cobrand_asset_urls?.logo_3);
      }
    }

  }

  private _checkUserAgreement(formValues: IRegistration): boolean {

      this._dialog.open(UserAgreementComponent, {
        width: '60%'
      }).afterClosed()
        .subscribe((result : any) => {

        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.userRegistrationForm.markAsPristine();

        if (result === true) {
          const submitValues = this.oktaRegistrationEnabled
            ? {
              ...formValues,
              okta_user_id: this._oktaUserId,
              OktaExists: this._oktaExists
            }
            : formValues;

          if (!this._token)
            this._registerUser({ ...submitValues, bSignature: result });

          else
            this._registerUser({ ...submitValues, bSignature: result, source: this._token, fromURL: true });

        } else {
          this.hasError = true;
          this._showRegistrationMessage('Your registration was not completed due to not accepting the web agreement. Please submit again if done in error or close page.');

        }
        return result;
      });

    return true;
  }

  private _registerUser(formValue: IRegistration) {
    this.userRegistrationForm.disable({ emitEvent: false });

    this.inProgress = true;
    this.loadingMessage = 'Creating a User Account. Please Wait.';

    formValue.phone = formValue.phone.replace(/[\(\)\s\-]*/g, '');
    formValue.mphone = formValue?.mphone.replace(/[\(\)\s\-]*/g, '');
    formValue.fphone = formValue?.fphone.replace(/[\(\)\s\-]*/g, '');

    this._registrationService.registerUser(formValue)
      .pipe(first())
      .subscribe({
        next: (response) => {
          this._processRegistrationResponse(response);
        },
        error: (response : HttpErrorResponse) => {
          this._processRegistrationResponse(response.error, true);
        }
      });
  }

  private _processRegistrationResponse(response : any, error: boolean = false) {
    this.hasError = response.success == 0 || error;
    this.inProgress = false;
    this.loadingMessage = '';

    if (response) {
      const msg = response.message;

      if (this.hasError) {
        this.userRegistrationForm.enable({ emitEvent: false });

        if (msg.indexOf('already in use') > -1) {
          //username already taken, prompt to pick a different one
          this._showRegistrationMessage('Username already exists. Choose a different username or click on <a target="_blank" href="' + environment.authClient + 'pwRequest' + '">forgot password</a> or contact us at 1-877-645-0147.');

        } else {
          this._showRegistrationMessage(msg);

        }

      } else {

        //registration completed but account is disabled
        if (response.disabled == 'true')  {
          this.disabledAccount = true;
          this._showRegistrationMessage('Thank you for registering for Lead Advantage Pro. Your profile was created but made inaccessible until your credentials can be validated. You will be contacted within 24 hours or you can call 1-877-645-0147.');

        }

        else
          this._showRegistrationMessage('Your account has been created, please <a class="underline" href="' + environment.authClient + '">click here</a> to log on to Lead Advantage Pro.');

      }

    } else {
      this.userRegistrationForm.enable({ emitEvent: false });
      this._showRegistrationMessage('An unexpected error occurred. Please try again.');

    }

  }
  private _initializeRegistrationPage(): void {
    this._activeRoute.queryParams
      .pipe(first(), takeUntil(this._componentDestroyed$))
      .subscribe(params => {
        this._token = params?.q;
        this._email = params?.e ? decodeURIComponent(params?.e) : '';

        if (this._token && this._token.length > 0)
          this._registrationService.validateUrl(this._token)
            .pipe(first())
            .subscribe({
              next: (cobrandAccount: IValidateUrl) => {
                this._checkURLValidation(cobrandAccount)

              },
              error: (err : HttpErrorResponse) => {
                this.displayInvalidAcct = true;

              }
            })
            .add(() => {
              this.componentLoaded = true;
              if (this.displayInvalidAcct || this.displayRoleExpired)
                this.userRegistrationForm.disable({emitEvent: false});

            });

        else {
          this.componentLoaded = true;
          this.setLogo('assets/images/LOGO_Lead-Advantage-Pro_Blue.png');
        }

        if(this._email && this._email.length > 0) {
          this.userRegistrationForm.get('email')?.setValue(this._email);
          this.userRegistrationForm.get('email')?.disable({ emitEvent: false });
        }

      });
  }

  private _showRegistrationMessage(message: string) {
    this.registrationAttempt = true
    this._toastMsg.resetMessage();
    this.registrationMessage$.next(message);
  }

  private updatePhoneValidation() {
    const phoneControl = this.userRegistrationForm.get('phone');
    const mphoneControl = this.userRegistrationForm.get('mphone');
    if (phoneControl?.value || mphoneControl?.value) {
      phoneControl?.clearValidators();
      mphoneControl?.clearValidators();
    } else {
      phoneControl?.setValidators([Validators.required, Validators.pattern(this._phonePattern)]);
      mphoneControl?.setValidators([Validators.required, Validators.pattern(this._phonePattern)]);
    }
    phoneControl?.updateValueAndValidity();
    mphoneControl?.updateValueAndValidity();
  }

  private _handleEligibilityResponse(response: IOktaRegistrationEligibilityResponse, emailValue: string): void {
    const result = response?.result;
    const nextStep = result?.next_step || '';

    if (!response?.success || !result || !nextStep) {
      this._resetEligibilityResult();
      this.eligibilityBlocked = true;
      this.eligibilityMessage = response?.message || 'Unable to determine registration eligibility. Please try again or contact support.';
      return;
    }

    if (nextStep === 'stop_contact_support' ) {
      this._resetEligibilityResult();
      this.eligibilityBlocked = true;
      this.showLoginCta = false;
      this.eligibilityMessage = response.message || 'Please contact support to complete registration.';
      return;
    }

    if (nextStep === 'stop_exists_both') {
      this._resetEligibilityResult();
      this.eligibilityBlocked = true;
      this.showLoginCta = true;
      this.eligibilityMessage = response.message || 'An account already exists for this email. Please continue to login.';
      return;
    }

    if (nextStep !== 'proceed_full_registration' && nextStep !== 'proceed_okta_exists') {
      this._resetEligibilityResult();
      this.eligibilityBlocked = true;
      this.eligibilityMessage = 'This registration state is not currently supported. Please contact support.';
      return;
    }

    this.eligibilityCheckComplete = true;
    this.eligibilityBlocked = false;
    this.eligibilityMessage = '';
    this._oktaUserId = result.okta_user_id || '';
    this._oktaExists = !!result.exists_okta;

    this.email.enable({ emitEvent: false });
    const normalizedEmail = (result.email || emailValue).toLowerCase();
    this.email.setValue(normalizedEmail, { emitEvent: false });
    this.login.setValue(normalizedEmail, { emitEvent: false });
    this.login.disable({ emitEvent: false });
    this.email.disable({ emitEvent: false });

    if (nextStep === 'proceed_okta_exists') {
      this.showLoginField = false;
      this._setPasswordRequirements(false);
      this._prefillFromOkta(result.user);
      return;
    }

    this.showLoginField = true;
    this._setPasswordRequirements(true);
  }

  private _setPasswordRequirements(required: boolean): void {
    this.showPasswordFields = required;

    if (required) {
      this.password.setValidators([Validators.required, Validators.pattern(this._pwordPattern)]);
      this.password.enable({ emitEvent: false });
      this.confirmPwd.enable({ emitEvent: false });
      this.password.updateValueAndValidity({ emitEvent: false });
      this.confirmPwd.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.password.setValue('', { emitEvent: false });
    this.confirmPwd.setValue('', { emitEvent: false });
    this.password.clearValidators();
    this.confirmPwd.clearValidators();
    this.password.setErrors(null);
    this.confirmPwd.setErrors(null);
    this.password.disable({ emitEvent: false });
    this.confirmPwd.disable({ emitEvent: false });
    this.password.updateValueAndValidity({ emitEvent: false });
    this.confirmPwd.updateValueAndValidity({ emitEvent: false });
  }

  private _prefillFromOkta(oktaUser?: IOktaRegistrationUser): void {
    if (!oktaUser) {
      return;
    }

    if (oktaUser.mobilePhone) {
      this.mphone.setValue(oktaUser.mobilePhone, { emitEvent: false });
    }
  }

  private _resetEligibilityResult(): void {
    this.eligibilityCheckComplete = false;
    this.eligibilityBlocked = false;
    this._oktaUserId = '';
    this._oktaExists = false;
    this.showLoginCta = false;
    this.showLoginField = true;
    this.login.enable({ emitEvent: false });
    this._setPasswordRequirements(true);
  }

  public closeNotice(){
    this.displayInvalidAcct = false;
    this.displayRoleExpired = false;
    this.displayAcctInfo = false;
    this.registrationAttempt = false;
  }

  ngOnDestroy(): void {
    this._componentDestroyed$.next(true);
    this._componentDestroyed$.complete();
  }

}
